import { onValue, ref } from "firebase/database";
import { database } from "../firebase/firebaseConfig";
import { CoreError, CoreErrorCause } from "../../domain/models/CoreError";
import {
  DAILY_CARDS_EXPERIMENT_ID,
  DailyCardsAssignment,
  DailyCardsExperimentConfig,
} from "../../domain/abTesting/models/DailyCardsExperiment";
import {
  parseDailyCardsAssignment,
  parseDailyCardsExperimentConfig,
} from "../../domain/abTesting/dailyCardsExperimentPolicy";

const DEFINITIONS_PATH = "abTesting/definitions";
const ASSIGNMENTS_PATH = "abTesting/assignments";

/**
 * RTDB-driven A/B tests. Two server-owned subtrees:
 *   - `abTesting/definitions/{experimentId}` — admin-written, signed-in read.
 *   - `abTesting/assignments/{uid}/{experimentId}` — Cloud Functions only,
 *     readable by that user.
 * Both are observed live so a console flip applies without a page reload.
 */
export class AbTestingService {
  private static instance: AbTestingService;

  private constructor() {}

  public static getInstance(): AbTestingService {
    if (!AbTestingService.instance) {
      AbTestingService.instance = new AbTestingService();
    }
    return AbTestingService.instance;
  }

  public observeDailyCardsDefinition(
    onChanged: (definition: DailyCardsExperimentConfig | null) => void,
    onError: (error: CoreError) => void,
  ): () => void {
    return this.observe(
      `${DEFINITIONS_PATH}/${DAILY_CARDS_EXPERIMENT_ID}`,
      parseDailyCardsExperimentConfig,
      onChanged,
      onError,
      "Failed to observe daily-cards experiment definition.",
    );
  }

  public observeDailyCardsAssignment(
    userId: string,
    onChanged: (assignment: DailyCardsAssignment | null) => void,
    onError: (error: CoreError) => void,
  ): () => void {
    return this.observe(
      `${ASSIGNMENTS_PATH}/${userId}/${DAILY_CARDS_EXPERIMENT_ID}`,
      parseDailyCardsAssignment,
      onChanged,
      onError,
      "Failed to observe daily-cards experiment assignment.",
    );
  }

  private observe<T>(
    path: string,
    parse: (raw: unknown) => T | null,
    onChanged: (value: T | null) => void,
    onError: (error: CoreError) => void,
    errorContext: string,
  ): () => void {
    const unsubscribe = onValue(
      ref(database, path),
      (snapshot) => {
        onChanged(snapshot.exists() ? parse(snapshot.val()) : null);
      },
      (error) => {
        onError(
          new CoreError({
            cause: CoreErrorCause.AbTestingLoadFailed,
            context: errorContext,
            underlyingException: error,
            // A failed read only means "experiment off" for this session.
            reportToSentry: false,
          }),
        );
      },
    );

    return () => {
      unsubscribe();
    };
  }
}
